import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import {
  categoryPensionOptions,
  optStateNestPensionOptions,
  PensionProvidersName,
} from '@nxsys/constants/AppConstants';
import { PensionProvidersNameEnum } from '@nxsys/constants/AppEnums';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Option } from '@nxsys/types/models/Common';
import { ICompany } from '@nxsys/types/models/company';
import { Col, Form, Row } from 'antd';
import { FormProps } from 'antd/lib';
import { Moment } from 'moment';
import React, { Ref, useImperativeHandle } from 'react';

export type PensionValues = {
  pensionCategory: Option<string> | null;
  pensionStatus: Option<string> | null;
  dateOnClassifiedOn: Moment | null;
  lastAssessmentOn: Moment | null;
  pensionProviderName: string;
  membershipId: string;
  postponementEndDate: Moment | null;
  membershipStartDate: Moment | null;
  otpState: Option<string> | null;
  otpInDate: Moment | null;
  otpOutDate: Moment | null;
};

export type PensionFormRef = {
  isValidForm: () => Promise<boolean>;
  getValues: () => PensionValues;
  resetFields: () => void;
};

type PensionFormProps = {
  formRef?: Ref<PensionFormRef>;
  initialValues?: PensionValues;
  employer?: ICompany;
};

const PensionForm: React.FC<PensionFormProps> = ({
  formRef,
  initialValues,
  employer,
}) => {
  const { messages } = useTypedIntl();

  const [form] = Form.useForm<PensionValues>();

  // The selected company's pension provider drives whether these fields can be
  // edited. When the company uses Smart Pension via the SMART API (not managed
  // manually), the values are sourced from SMART and cannot be edited here.
  const isSmartPension =
    employer?.autoEnrolSettings?.pensionProviderId ===
    PensionProvidersNameEnum.SMART_PENSION;
  const isManagePensionManually =
    !!employer?.autoEnrolSettings?.managePensionManually;
  const isSmartManaged = isSmartPension && !isManagePensionManually;

  useImperativeHandle(formRef, () => ({
    isValidForm: async () => {
      try {
        await form.validateFields();
        return true;
      } catch (error) {
        return false;
      }
    },
    getValues() {
      return form.getFieldsValue(true);
    },
    resetFields() {
      form.resetFields();
    },
  }));

  const onFinish: FormProps['onFinish'] = async (values) => {
    console.info('values', values);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      initialValues={{
        ...initialValues,
        pensionProviderName:
          PensionProvidersName[
            employer?.autoEnrolSettings
              ?.pensionProviderId as keyof typeof PensionProvidersName
          ] || '',
      }}
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
    >
      <div className="text-[18px] font-bold mb-[40px]">
        {messages['common.pension']}
      </div>
      <div className="text-[16px] mb-[24px]">
        {messages['common.autoEnrollmentStatus']}
      </div>
      <Row gutter={24}>
        <Col sm={24} md={12}>
          <Form.Item name="pensionCategory" label={messages['common.category']}>
            <AppSelect
              options={categoryPensionOptions}
              placeholder={messages['form.select']}
              className="w-full !min-w-0"
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item name="pensionStatus" label={messages['common.status']}>
            <AppSelect
              options={[]}
              placeholder={messages['form.select']}
              className="w-full !min-w-0"
              disabled
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            name="dateOnClassifiedOn"
            label={messages['common.dateOnClassifiedOn']}
          >
            <AppDatePicker
              placeholder={messages['form.selectDate']}
              format="DD/MM/YYYY"
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            name="lastAssessmentOn"
            label={messages['common.lastAssessmentDoneOn']}
          >
            <AppDatePicker
              placeholder={messages['form.selectDate']}
              format="DD/MM/YYYY"
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            name="pensionProviderName"
            label={messages['common.pensionProviderName']}
          >
            <AppInput placeholder={messages['form.enter']} disabled />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            name="membershipId"
            label={messages['common.membershipId']}
          >
            <AppInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            name="postponementEndDate"
            label={messages['common.postponementEndDate']}
          >
            <AppDatePicker
              placeholder={messages['form.selectDate']}
              format="DD/MM/YYYY"
              disabled={isSmartManaged}
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            name="membershipStartDate"
            label={messages['common.membershipStartDate']}
          >
            <AppDatePicker
              placeholder={messages['form.selectDate']}
              format="DD/MM/YYYY"
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item name="otpState" label={messages['common.otpState']}>
            <AppSelect
              options={optStateNestPensionOptions}
              placeholder={messages['form.select']}
              disabled={isSmartManaged}
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item name="otpInDate" label={messages['common.otpInDate']}>
            <AppDatePicker
              placeholder={messages['form.selectDate']}
              format="DD/MM/YYYY"
              disabled={isSmartManaged}
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item name="otpOutDate" label={messages['common.otpOutDate']}>
            <AppDatePicker
              placeholder={messages['form.selectDate']}
              format="DD/MM/YYYY"
              disabled={isSmartManaged}
            />
          </Form.Item>
        </Col>
      </Row>
    </AppFormContainer>
  );
};

export default PensionForm;
